import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import useCountUp from "@/hooks/useCountUp";
import { MessageSquare, CheckCheck, XCircle, Eye, RotateCw, Download, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const inputCls =
  "border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

function StatusBadge({ status }) {
  const map = {
    queued: "text-slate-300 bg-white/10",
    sent: "text-blue-400 bg-blue-500/10",
    delivered: "text-green-400 bg-green-500/10",
    read: "text-sky-400 bg-sky-500/10",
    failed: "text-red-400 bg-red-500/10",
  };
  return (
    <span className={`text-[10px] tracking-[0.1em] uppercase font-bold rounded px-2 py-0.5 ${map[status] || "text-slate-300 bg-white/10"}`}>
      {status || "—"}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  const display = useCountUp(value);
  return (
    <motion.div
      variants={sectionVariants}
      data-testid={testid}
      className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30"
    >
      <div className="flex items-center justify-between">
        <p className={labelCls}>{label}</p>
        <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
          <Icon className="h-4 w-4 text-orange-600" />
        </div>
      </div>
      <p className="font-heading text-2xl font-black text-white mt-2">{display}</p>
    </motion.div>
  );
}

export default function Reports() {
  const { activeBranchId } = useBranch();
  const [summary, setSummary] = useState({ total: 0, delivered: 0, read: 0, failed: 0 });
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [filters, setFilters] = useState({ status: "all", type: "all", template: "all", date_from: "", date_to: "" });
  const [usedTemplates, setUsedTemplates] = useState([]);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const params = { branch_id: activeBranchId };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.type !== "all") params.type = filters.type;
      if (filters.template !== "all") params.template = filters.template;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [s, m] = await Promise.all([
        api.get("/reports/summary", { params: { branch_id: activeBranchId } }),
        api.get("/messages", { params }),
      ]);
      setSummary(s.data);
      setMessages(m.data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!activeBranchId) return;
    api
      .get("/conversations/used-templates", { params: { branch_id: activeBranchId } })
      .then(({ data }) => setUsedTemplates(data))
      .catch(() => setUsedTemplates([]));
  }, [activeBranchId]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/messages/${id}/retry`);
      toast.success("Message re-sent");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRetrying(null);
    }
  };

  const exportCsv = () => {
    const header = ["time", "contact_name", "mobile", "template_name", "type", "status", "failure_reason"];
    const rows = messages.map((m) => [
      m.created_at || "",
      m.contact_name || "",
      m.mobile || "",
      m.template_name || (m.message_text ? "text" : ""),
      m.message_type || "",
      m.status || "",
      (m.failure_reason || "").replace(/[\n,]/g, " "),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `messages_report_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtTime = (v) => (v ? new Date(v).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

  return (
    <div data-testid="reports-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Reports</h1>
          <p className="text-sm text-slate-500 mt-2">Message delivery report for this branch.</p>
        </div>
        <button data-testid="export-csv-button" onClick={exportCsv}
          className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 flex items-center gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      >
        <StatCard testid="stat-total" icon={MessageSquare} label="Total Messages" value={summary.total} />
        <StatCard testid="stat-delivered" icon={CheckCheck} label="Delivered" value={summary.delivered} />
        <StatCard testid="stat-failed" icon={XCircle} label="Failed" value={summary.failed} />
        <StatCard testid="stat-read" icon={Eye} label="Read" value={summary.read} />
      </motion.div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-5 bg-white/5 border border-white/10 rounded-lg p-4">
        <div>
          <p className={labelCls}>Status</p>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status" className="mt-1.5 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "queued", "sent", "delivered", "read", "failed"].map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className={labelCls}>Type</p>
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger data-testid="filter-type" className="mt-1.5 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "broadcast", "utility", "reply"].map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className={labelCls}>Template</p>
          <Select value={filters.template} onValueChange={(v) => setFilters({ ...filters, template: v })}>
            <SelectTrigger data-testid="filter-template" className="mt-1.5 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Templates</SelectItem>
              {usedTemplates.map((name) => (
                <SelectItem key={name} value={name} data-testid={`filter-template-opt-${name}`}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className={labelCls}>From</p>
          <input data-testid="filter-date-from" type="date" className={`mt-1.5 ${inputCls}`}
            value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </div>
        <div>
          <p className={labelCls}>To</p>
          <input data-testid="filter-date-to" type="date" className={`mt-1.5 ${inputCls}`}
            value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </div>
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden bg-[#141414]">
        <Table>
          <TableHeader>
            <TableRow className="bg-white/5">
              {["Time", "Contact", "Mobile", "Template", "Status", "Failure"].map((h) => (
                <TableHead key={h} className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{h}</TableHead>
              ))}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
            ) : messages.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-slate-400">No messages match these filters.</TableCell></TableRow>
            ) : (
              messages.map((m) => (
                <TableRow key={m.id} data-testid={`message-row-${m.id}`}>
                  <TableCell className="text-sm text-slate-300 whitespace-nowrap">{fmtTime(m.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium text-white">{m.contact_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-300">{m.mobile || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-300">{m.template_name || (m.message_text ? "text" : "—")}</TableCell>
                  <TableCell><StatusBadge status={m.status} /></TableCell>
                  <TableCell className="text-xs text-red-600 max-w-[200px] truncate">{m.failure_reason || ""}</TableCell>
                  <TableCell className="text-right">
                    {m.status === "failed" && (
                      <button data-testid={`retry-${m.id}`} onClick={() => retry(m.id)} disabled={retrying === m.id}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-400 disabled:opacity-60">
                        {retrying === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                        Retry
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
